import React, { useState, useEffect, useRef } from "react";
import "./Contact.css";
import { sendContact as sendContactAPI } from "../services/contactService";
import { useTranslation } from "react-i18next";
import formimg1 from "../img/contactleft.png";

/* ─────────────────────────────────────────────────────
   HOOK — observe scroll visibility
───────────────────────────────────────────────────── */
function useVisible(ref) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.15 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [ref]);
  return visible;
}

/* ─────────────────────────────────────────────────────
   ICONS
───────────────────────────────────────────────────── */
const IconPin = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#FFF8F0"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
    <circle cx="12" cy="9" r="2.5" />
  </svg>
);

const IconPhone = ({ color = "#FFF8F0" }) => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 10.8 19.79 19.79 0 01.22 2.18 2 2 0 012.22 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 14.92z" />
  </svg>
);

const IconMail = ({ color = "#FFF8F0" }) => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <polyline points="2,4 12,13 22,4" />
  </svg>
);

const IconChat = ({ color = "#7A0C2E" }) => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
  </svg>
);

/* ═══════════════════════════════════════════════════
   SECTION 1 — HERO
═══════════════════════════════════════════════════ */
function HeroSection() {
  const [visible, setVisible] = useState(false);
  const { t } = useTranslation();
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  return (
    <section className="cp-hero">
      <div className="cp-hero-ring" />
      <div className="cp-hero-bg" />
      <div className={`cp-hero-content${visible ? " cp-visible" : ""}`}>
        <p className="cp-hero-eyebrow">{t("listening_title")}</p>
        <h1 className="cp-hero-title">
          {t("listening_subtitle")}
        </h1>
        <p className="cp-hero-desc">
          {t("listening_description")}
        </p>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════
   SECTION 2 — OUR DETAILS
═══════════════════════════════════════════════════ */


function DetailsSection() {
  const ref = useRef(null);
  const visible = useVisible(ref);
  const { t } = useTranslation();
  const DETAIL_CARDS = [
  {
    icon: <IconPin />,
    title: t("office_title"),
    lines: [
     t("office_address")
    ],
  },
  {
    icon: <IconPhone />,
    title: t("contact_us_title")  + t("contact_us_title1") + " " + t("contact-us_title2"),
    lines: ["1800-123-456", "+91 44 1234 5678"],
  },
  {
    icon: <IconMail />,
    title: t("email_us_title"),
    lines: ["hello@niranmatrimony.com", "support@niranmatrimony.com"],
  },
];
  return (
    <section ref={ref} className={`cp-details${visible ? " cp-visible" : ""}`}>
      <div className="cp-details-arch" />
      <div className="cp-details-header">
        <p className="cp-details-eyebrow">{t("find_us_title")}</p>
        <h2 className="cp-details-title">{t("our_details_title")}</h2>
      </div>
      <div className="cp-details-grid">
        {DETAIL_CARDS.map((card) => (
          <div className="cp-details-card" key={card.title}>
            <div className="cp-details-icon">{card.icon}</div>
            <h3 className="cp-details-card-title">{card.title}</h3>
            <p className="cp-details-card-text">
              {card.lines.map((line, i) => (
                <React.Fragment key={i}>
                  {line}
                  {i < card.lines.length - 1 && <br />}
                </React.Fragment>
              ))}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════
   SECTION 3 — SEND A MESSAGE FORM
   ✅ API: POST /api/contact/create
   ✅ Payload keys:
        name        → form.name
        email       → form.email
        contact     → form.phone
        helpContent → form.topic
        message     → form.message
═══════════════════════════════════════════════════ */
function FormSection() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    topic: "",
    message: "",
  });
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState({ show: false, type: "", msg: "" });

  const showToast = (type, msg) => {
    setToast({ show: true, type, msg });
    setTimeout(() => setToast({ show: false, type: "", msg: "" }), 4000);
  };

  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      showToast("error", t("required_fields_error"));
      return;
    }
    setSubmitting(true);
    try {
      await sendContactAPI({
        name: form.name,
        email: form.email,
        contact: form.phone, // maps phone → contact
        helpContent: form.topic, // maps topic → helpContent ✅
        message: form.message,
      });
      showToast("success", t("message_sent_success"));
      setForm({ name: "", phone: "", email: "", topic: "", message: "" });
    } catch (err) {
      const msg =
        err?.response?.data?.message || t("message_failed");
      showToast("error", msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="cp-form-section">
      {toast.show && (
        <div className={`cp-toast ${toast.type}`}>
          <span className="cp-toast-icon">
            {toast.type === t("success") ? "✅" : "❌"}
          </span>
          <span>{toast.msg}</span>
          <button
            className="cp-toast-close"
            onClick={() => setToast({ show: false })}
          >
            ✕
          </button>
        </div>
      )}

      <p className="cp-form-eyebrow">{t("send_message_title")}</p>

      <div className="cp-form-inner">
        {/* Left — image */}
        <div className="cp-form-image-pane">
          <img src={formimg1} alt="Indian wedding couple holding hands" />
          <div className="cp-form-image-frame" />
        </div>

        {/* Right — form */}
        <div className="cp-form-pane">
          <h2 className="cp-form-title">{t("lets_talk_title")}</h2>
          <p className="cp-form-subtitle">
            {t("lets_talk_description")}
          </p>

          <form onSubmit={handleSubmit} noValidate>
            <div className="cp-form-row">
              <div className="cp-field">
                <label className="cp-label">{t("your_name_label")}</label>
                <input
                  className="cp-input"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder={t("placeholder_name")}
                />
              </div>
              <div className="cp-field">
                <label className="cp-label">{t("phone_number_label")}</label>
                <input
                  className="cp-input"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="+91 98765 43210"
                />
              </div>
            </div>

            <div className="cp-field">
              <label className="cp-label">{t("email_address_label")}</label>
              <input
                className="cp-input"
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="priya@example.com"
              />
            </div>

            <div className="cp-field">
              <label className="cp-label">{t("help_label")}</label>
              <select
                className={`cp-select${form.topic ? " has-value" : ""}`}
                name="topic"
                value={form.topic}
                onChange={handleChange}
              >
                <option value="" disabled>
                  {t("select_topic_placeholder")}
                </option>
                <option value="Create / Update Profile">
                  {t("select_topic_create_profile")}
                </option>
                <option value="Match Suggestions">
                  {t("select_topic_match_suggestions")}
                </option>
                <option value="Technical Support">
                  {t("select_topic_technical_support")}
                </option>
                <option value="Plans & Billing">
                  {t("select_topic_plans_billing")}
                </option>
                <option value="Other">
                  {t("select_topic_other")}
                </option>
              </select>
            </div>

            <div className="cp-field">
              <label className="cp-label">{t("message_label")}</label>
              <textarea
                className="cp-textarea"
                name="message"
                rows={4}
                value={form.message}
                onChange={handleChange}
                placeholder={t("placeholder_message")}
              />
            </div>

            <p className="cp-privacy">
              {t("privacy_note")}
            </p>

            <button
              type="submit"
              className="cp-submit-btn1"
              disabled={submitting}
            >
              {submitting ? <span className="cp-spinner" /> : null}
              {submitting ? t("sending") : t("send_button")}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════
   SECTION 4 — REACH US ANYTIME + QUOTE
═══════════════════════════════════════════════════ */


function ReachSection() {
  const ref = useRef(null);
  const visible = useVisible(ref);
  const { t } = useTranslation();

const REACH_CARDS = [
  {
    icon: <IconPhone color="#7A0C2E" />,
    title: t("call_us_title"),
    text: t("call_us_description"),
  },
  {
    icon: <IconChat />,
    title: t("chat_us_title"),
    text: t("chat_us_description"),
  },
  {
    icon: <IconMail color="#7A0C2E" />,
    title: t("email_contact_title"),
    text: t("email_us_description"),
  },
];

  return (
    <section ref={ref} className={`cp-reach${visible ? " cp-visible" : ""}`}>
      <div className="cp-reach-header">
        <p className="cp-reach-eyebrow">{t("reach_us_title")}</p>
        <h2 className="cp-reach-title">
          {t("reach_us_description_1")}
        </h2>
        <p className="cp-reach-desc">
          {t("reach_us_description_2")}
        </p>
      </div>
      <div className="cp-reach-grid">
        {REACH_CARDS.map((card) => (
          <div className="cp-reach-card" key={card.title}>
            <div className="cp-reach-card-icon">{card.icon}</div>
            <h3 className="cp-reach-card-title">{card.title}</h3>
            <p className="cp-reach-card-text">{card.text}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function QuoteSection() {
  const { t } = useTranslation();
  const ref = useRef(null);
  const visible = useVisible(ref);

  return (
    <div className="cp-quote-strip">
      <p ref={ref} className={`cp-quote${visible ? " cp-visible" : ""}`}>
        "{t("journey_privacy_message")}"
      </p>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   DEFAULT EXPORT
   No props required — API is wired internally via contactService
═══════════════════════════════════════════════════ */
export default function ContactPage() {
  return (
    <main className="cp-root">
      <HeroSection />
      <DetailsSection />
      <FormSection />
      <ReachSection />
      <QuoteSection />
    </main>
  );
}
